import { Link } from 'react-router-dom';
import { FiTrash2, FiArrowRight, FiShoppingBag } from 'react-icons/fi';
import { useCart } from '../context/CartContext';

export default function CartPage() {
  const { cart, removeFromCart, updateQuantity, cartTotal, shippingFee, cartCount } = useCart();

  if (cartCount === 0) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      <FiShoppingBag size={64} className="text-gray-200 mx-auto mb-6" />
      <h2 className="font-display text-3xl font-bold text-yamass-black mb-3">Your cart is empty</h2>
      <p className="text-gray-400 mb-8">Add some products to continue shopping</p>
      <Link to="/shop" className="inline-flex items-center gap-2 bg-yamass-black text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-yamass-accent transition-colors">
        Start Shopping <FiArrowRight />
      </Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-4xl font-bold text-yamass-black mb-10">Shopping Cart</h1>

      <div className="grid lg:grid-cols-3 gap-10">
        {/* Items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.map(item => (
            <div key={`${item._id}-${item.size}`} className="flex gap-4 bg-yamass-cream rounded-2xl p-4">
              {/* Image */}
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-white flex-shrink-0">
                {item.images?.[0] ? (
                  <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl">👕</div>
                )}
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0">
                <Link to={`/product/${item._id}`} className="font-medium text-yamass-black hover:text-yamass-accent text-sm line-clamp-2">
                  {item.name}
                </Link>
                <p className="text-xs text-gray-400 mt-1">Size: {item.size}</p>
                <p className="text-yamass-accent font-semibold mt-1">
                  ৳{((item.salePrice || item.price) * item.quantity).toLocaleString()}
                </p>
              </div>

              {/* Qty + Remove */}
              <div className="flex flex-col items-end gap-3">
                <button onClick={() => removeFromCart(item._id, item.size)} className="text-gray-300 hover:text-red-400 transition-colors">
                  <FiTrash2 size={15} />
                </button>
                <div className="flex items-center border border-gray-200 rounded-full bg-white overflow-hidden">
                  <button onClick={() => updateQuantity(item._id, item.size, item.quantity - 1)} className="px-3 py-1 text-sm hover:bg-gray-50">−</button>
                  <span className="px-3 py-1 text-sm font-medium">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item._id, item.size, item.quantity + 1)} className="px-3 py-1 text-sm hover:bg-gray-50">+</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-yamass-cream rounded-2xl p-6 h-fit sticky top-24">
          <h2 className="font-display text-xl font-bold text-yamass-black mb-6">Order Summary</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span className="font-medium text-yamass-black">৳{cartTotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Shipping</span>
              <span className={`font-medium ${shippingFee === 0 ? 'text-green-600' : 'text-yamass-black'}`}>
                {shippingFee === 0 ? 'Free' : `৳${shippingFee}`}
              </span>
            </div>
            {shippingFee > 0 && (
              <p className="text-xs text-yamass-accent">Add ৳{(1000 - cartTotal).toLocaleString()} more for free shipping</p>
            )}
            <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-base">
              <span>Total</span>
              <span className="text-yamass-black">৳{(cartTotal + shippingFee).toLocaleString()}</span>
            </div>
          </div>
          <Link
            to="/checkout"
            className="mt-6 flex items-center justify-center gap-2 bg-yamass-black text-white py-4 rounded-full text-sm font-semibold hover:bg-yamass-accent transition-colors w-full"
          >
            Proceed to Checkout <FiArrowRight />
          </Link>
          <Link to="/shop" className="mt-3 block text-center text-sm text-gray-400 hover:text-yamass-black transition-colors">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
